import { z } from "zod";
import {
  AgentStreamResponse,
  agentStreamResponseSchema,
} from "@altimateai/ui-components/chatbotV2";

// Session Status Enum (reflects the conversation/turn state, not lifecycle)
export const sessionStatusSchema = z.enum(["failed", "completed", "pending", "cancelled"]);
export type SessionStatusEnum = z.infer<typeof sessionStatusSchema>;

// Sidebar run-status: derived from the latest turn on the backend.
// - "running"   → turn is in flight (persisted server-side, so navigating away
//                 still shows the spinner on the row)
// - "succeeded" → latest turn completed normally (also for empty sessions)
// - "failed"    → latest turn errored / timed out / was cancelled
export const sessionRunStatusSchema = z.enum(["running", "succeeded", "failed"]);
export type SessionRunStatus = z.infer<typeof sessionRunStatusSchema>;

// Lifecycle status (active / archived / deleted) — controls whether a
// session is shown in the sidebar at all. The transitions are
// user-driven only: archived sessions stay archived indefinitely
// until the user explicitly deletes them. There is no automatic
// archived → deleted promotion. ``deleted`` rows stay in the DB
// (a separate backend pipeline handles eventual hard removal) but
// are hidden from every FE listing.
export const sessionLifecycleStatusSchema = z.enum(["active", "archived", "deleted"]);
export type SessionLifecycleStatus = z.infer<typeof sessionLifecycleStatusSchema>;

// Filter accepted by GET /studio/sessions ?status=
export const sessionLifecycleListFilterSchema = z.enum(["active", "archived", "all"]);
export type SessionLifecycleListFilter = z.infer<typeof sessionLifecycleListFilterSchema>;

// Playground Session Types
export const playgroundSessionSchema = z.object({
  session_id: z.string(),
  name: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
  status: sessionStatusSchema,
  datamates: z.array(z.coerce.string()).optional().nullable(),
  knowledge: z.array(z.string()).optional().nullable(),
  context: z.record(z.unknown()).optional().nullable(),
  // One-line intent summary for the conversation — lazily generated by
  // the backend (Haiku). NULL/undefined means "not generated yet";
  // sidebar falls back to `name` (original question). Named
  // `session_intent` to avoid confusion with the per-turn `summary`
  // field on chat blocks.
  session_intent: z.string().optional().nullable(),
  // Sidebar row indicator — derived from the latest turn (see
  // ``sessionRunStatusSchema``). Drives the spinner / green / red dot.
  run_status: sessionRunStatusSchema,
  // True iff the session has at least one scheduled report attached.
  // Drives the bell icon on the row.
  has_scheduled_report: z.boolean(),
  // Rule id of that report — the same value as ``RuleResponse.id`` from
  // /api/v1/alerts/rules, so it deep-links straight to the subscription
  // detail page. Null when no report is attached, and absent on older
  // backends.
  scheduled_report_rule_id: z.number().nullish(),
  // When the user starred the session; null when not starred. The list puts
  // starred sessions first, most recently starred on top. Absent on older
  // backends.
  starred_at: z.string().nullish(),
});

// Status-update response from PATCH /studio/sessions/{id}
export const sessionStatusUpdateResponseSchema = z.object({
  session_id: z.string(),
  status: sessionLifecycleStatusSchema,
});
export type SessionStatusUpdateResponse = z.infer<typeof sessionStatusUpdateResponseSchema>;

// Rename response from PATCH /studio/sessions/{id}/name. The name becomes the
// sidebar title, ``session_intent``.
export const sessionRenameResponseSchema = z.object({
  session_id: z.string(),
  session_intent: z.string(),
});
export type SessionRenameResponse = z.infer<typeof sessionRenameResponseSchema>;

// Star response from PATCH /studio/sessions/{id}/star; ``starred_at`` is null
// once unstarred.
export const sessionStarResponseSchema = z.object({
  session_id: z.string(),
  starred_at: z.string().nullable(),
});
export type SessionStarResponse = z.infer<typeof sessionStarResponseSchema>;

// ``user_sessions.session_intent`` is VARCHAR(200); the backend rejects longer
// names after trimming.
export const SESSION_NAME_MAX_LENGTH = 200;

export type PlaygroundSession = z.infer<typeof playgroundSessionSchema>;

export const playgroundAllSessionResponseSchema = z.object({
  sessions: z.array(playgroundSessionSchema),
  total: z.number(),
  page: z.number(),
  page_size: z.number(),
  has_more: z.boolean(),
});

export type PlaygroundAllSessionResponse = z.infer<typeof playgroundAllSessionResponseSchema>;

// Attachment Schema
export const attachmentSchema = z.object({
  id: z.string(),
  attachment_name: z.string(),
  file_type: z.string().nullable(),
  s3_url: z.string().nullable(),
  extracted_content: z.string().nullable(),
  order: z.number(),
  attachment_type: z.string(),
  attachment_reference_id: z.string().nullable(),
  file_size: z.number().nullable(),
  created_on: z.string().transform(val => val), // Keep as ISO string for now
});

// Chat Block Types
export const chatBlockSchema = z.object({
  role: z.enum(["user", "assistant"]),
  content: z.array(agentStreamResponseSchema),
  id: z.coerce.string(),
  // transform string to epoch timestamp
  timestamp: z
    .string()
    .optional()
    .transform(val => {
      if (val) {
        const date = new Date(val);
        return date.getTime();
      }
      return Date.now(); // Fallback to current time if not provided
    }),
  attachments: z.array(attachmentSchema).optional().default([]),
});

export type ChatBlock = z.infer<typeof chatBlockSchema>;

// Playground Session Details Response
export const playgroundSessionDetailsResponseSchema = z.object({
  session: playgroundSessionSchema,
  turns: z.array(chatBlockSchema),
});

export type PlaygroundSessionDetailsResponse = z.infer<
  typeof playgroundSessionDetailsResponseSchema
>;

// TypeScript interfaces with proper typing (for runtime use)
export interface ChatBlockTyped {
  id: number;
  role: "user" | "assistant";
  content: AgentStreamResponse[];
}

export interface PlaygroundSessionDetailsResponseTyped {
  session: PlaygroundSession;
  turns: ChatBlockTyped[];
}

// Legacy interface for backward compatibility
export interface ChatHistoryItem {
  id: string;
  title: string;
  timestamp: string;
  messageCount: number;
}

// ========== Share Links (US-003) ==========

export const shareAccessTypeSchema = z.enum([
  "anyone",
  "specific_people",
  "all_altimate_users",
  "team",
]);
export type ShareAccessType = z.infer<typeof shareAccessTypeSchema>;

export const shareLinkSchema = z.object({
  id: z.string().uuid(),
  session_id: z.string(),
  access_type: shareAccessTypeSchema,
  expires_at: z.string().nullable(),
  created_by: z.number(),
  created_at: z.string(),
  title: z.string().nullable(),
  share_url: z.string(),
  allowed_users: z.array(z.number()).nullable(),
  allowed_teams: z.array(z.number()).nullable().optional(),
});

export type ShareLink = z.infer<typeof shareLinkSchema>;

export const shareLinkResponseSchema = shareLinkSchema;
export type ShareLinkResponse = ShareLink;

export const shareLinkListResponseSchema = z.object({
  share_links: z.array(shareLinkSchema),
  total: z.number(),
});
export type ShareLinkListResponse = z.infer<typeof shareLinkListResponseSchema>;

export const sharedSessionResponseSchema = z.object({
  session_id: z.string(),
  session_name: z.string(),
  created_at: z.string(),
  share_title: z.string().nullable(),
  turns: z.array(chatBlockSchema),
  is_read_only: z.boolean(),
});
export type SharedSessionResponse = z.infer<typeof sharedSessionResponseSchema>;

export interface ShareLinkCreateRequest {
  session_id: string;
  access_type: ShareAccessType;
  expires_in_days?: number | null;
  user_ids?: number[];
  team_ids?: number[];
  title?: string;
}

// ========== Slack Webhook Sharing (US-004) ==========

export const shareToSlackResponseSchema = z.object({
  success: z.boolean(),
  message: z.string().nullable().optional(),
});
export type ShareToSlackResponse = z.infer<typeof shareToSlackResponseSchema>;

export interface ShareToSlackRequest {
  session_id: string;
  webhook_id: number;
}
